<template>
  <el-main v-loading="request.loading" class="send-docutax-request">
    <div class="upload-section">
      <h1>Submit Tax Documents</h1>
      <div class="el-form-item">
        <label class="el-form-item__label">Request</label>
        <el-descriptions class="mb-3" title="" direction="vertical" border>
          <el-descriptions-item>
            <template slot="label">
              <i class="el-icon-user"></i>
              Preparer
            </template>
            {{ request.message }}
          </el-descriptions-item>
        </el-descriptions>
      </div>

      <el-form label-position="top" @submit.native.prevent>
        <el-form-item label="Response (optional)">
          <el-input type="textarea" maxlength="500" :autosize="{ minRows: 3, maxRows: 6 }"
            placeholder="Include a response to your tax preparer" v-model="documentSubmission.message" />
        </el-form-item>
      </el-form>

      <div class="el-form-item">
        <label class="el-form-item__label">Documents</label>
        <el-upload :disabled="!uploadConfiguration.isInitialized" class="uploader" drag
          :action="uploadConfiguration.action" :accept="uploadConfiguration.accept" :data="uploadConfiguration.data"
          :before-upload="onBeforeUpload" :on-error="onUploadError" :file-list="documentSubmission.documents"
          ref="uploader" :http-request="handleUploadRequest" multiple>
          <i class="el-icon-upload"></i>
          <div class="el-upload__text">
            Drop file here or <em>click</em> to take a picture or upload a file<br />
            <small>jpg/png/pdf files with a size less than 50mb</small>
          </div>
        </el-upload>
      </div>

      <el-row class="mt-3" type="flex" align="middle" justify="center">
        <el-button class="mt-3" v-loading="submitting" type="primary" @click="onSubmit" round
          style="min-width: 200px;">Submit</el-button>
      </el-row>
    </div>
    <el-dialog :visible="!this.accessCodeForm.verified && !request.hasResponded" title="Document Request Access Code"
      :show-close="false">
      <el-form :model="accessCodeForm.data" :rules="accessCodeForm.rules" ref="accessCodeForm" label-position="top"
        @submit.native.prevent>
        <p class="mt-0">A preparer at M & M Income Tax Service has requested your tax documents. Enter the access code
          sent alongside the request to continue.</p>
        <el-alert v-show="accessCodeForm.errorMessage" :title="accessCodeForm.errorMessage" :closable="false" show-icon
          type="error" />
        <el-form-item label="Access Code" required prop="accessCode">
          <el-input placeholder="Access Code" v-mask="'######'" inputmode="numeric"
            @keyup.enter.native="onValidateAccessCode" v-model="accessCodeForm.data.accessCode" />
        </el-form-item>
        <el-row type="flex" justify="center">
          <el-button v-loading="accessCodeForm.processing" class="ma-3" type="primary" round
            @click="onValidateAccessCode">Submit</el-button>
        </el-row>
      </el-form>
    </el-dialog>
    <el-dialog :visible="request.hasResponded" :show-close="false">
      <el-result icon="warning" title="Response Already Completed"
        subTitle="Your previous submission has been received and this request is now closed. Click 'Create New Request' and complete the form if you need to submit more documents.">
        <template slot="extra">
          <el-button type="primary" size="medium" @click="returnToHome">Create New Request</el-button>
        </template>
      </el-result>
    </el-dialog>
    <el-dialog :visible="responseIsComplete" :show-close="false">
      <el-result icon="success" title="Success!"
        subTitle="Our virtual tax pros will review your documents and prepare your tax return. Upon completion, the tax professional will contact you with any additional questions to finalize your tax return." />
    </el-dialog>
  </el-main>
</template>

<script>
import api from '@/api';
import RequestDetailsForReplyDto from '@/model/RequestDetailsForReplyDto';
import RequestResponseCreateDto from '@/model/RequestResponseCreateDto';
import elementDefaultUploadHandler from 'element-ui/packages/upload/src/ajax';

export default {
  props: {
    requestUrlSlug: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      accessCodeForm: {
        errorMessage: undefined,
        data: {
          accessCode: '',
        },
        verified: false,
        processing: false,
        rules: {
          accessCode: [{
            required: true,
            validator: (rule, value, callback) => {
              if (!value) {
                callback(new Error('Access Code is required.'));
              } else if (!value.match(/^[0-9]{6}$/i)) {
                callback(new Error('Access Code is invalid.'));
              } else {
                callback();
              }
            },
            trigger: 'blur',
          }],
        },
      },
      request: new RequestDetailsForReplyDto(),
      submitting: false,
      responseIsComplete: false,
      documentSubmission: {
        message: '',
        files: [],
      },
      uploadConfiguration: {
        isInitialized: false,
        action: '',
        data: undefined,
        accept: 'image/jpeg,image/png,application/pdf,image/heic,image/heic-sequence',
      },
      renamedFileTracker: {},
    };
  },
  methods: {
    configureUpload(uploadDetails) {
      this.uploadConfiguration.isInitialized = true;
      this.uploadConfiguration.action = uploadDetails.attributes.action;
      this.uploadConfiguration.data = uploadDetails.inputs;
    },
    handleUploadRequest(options) {
      // generates unique filename for each uploaded file
      const [originalFileName, extension] = options.file.name.split(/\.(?=[^.]+$)/);
      const uid = Math.random().toString(36).slice(-6);
      const uniqueFileName = `${originalFileName}-${uid}.${extension}`;
      this.renamedFileTracker[options.file.uid] = uniqueFileName;

      options.file = new File([options.file], uniqueFileName, {
        ...options.file,
      });
      elementDefaultUploadHandler(options);
    },
    onBeforeUpload(file) {
      if (file.size >= 52428800) {
        this.$message.error(`${file.name} is too large, file size must be less than 50mb.`);
        return false;
      }

      return true;
    },
    onUploadError(error, file) {
      console.error('upload failed', file, error);
      this.$message.error(`Failed to upload file named ${file.name}`);
    },
    async onValidateAccessCode() {
      try {
        await this.$refs.accessCodeForm.validate();
      } catch (e) {
        // failed validation
        return;
      }

      try {
        this.accessCodeForm.processing = true;
        this.accessCodeForm.errorMessage = undefined;

        const response = await api.docutax.validateAndLoadRequest(this.requestUrlSlug, this.accessCodeForm.data.accessCode);
        if (response.data) {
          this.request = new RequestDetailsForReplyDto(response.data);
          if (!this.request.hasResponded) {
            this.accessCodeForm.verified = true;
            this.configureUpload(this.request.uploadUrl);
          } else {
            this.accessCodeForm.errorMessage = 'This request is closed because it has already received a response';
          }
        }
      } catch (e) {
        this.accessCodeForm.errorMessage = 'The access code you entered is invalid. The access code is in the text message or email sent from your tax preparer.';
        this.$message.error('Access code is invalid');
      } finally {
        this.accessCodeForm.processing = false;
      }
    },
    async onSubmit() {
      const files = this.$refs.uploader.uploadFiles;
      if (files.length === 0) {
        if (this.documentSubmission.message.trim().length === 0) {
          this.$message.error('Please upload a file or include a message to your tax preparer.');
          return;
        }

        try {
          await this.$confirm('No files have been included. Only your message will be sent to your preparer. Continue?');
        } catch (e) {
          // user hit cancel
          return;
        }
      }

      const incompleteFiles = files.filter(file => file.status !== 'success');
      if (incompleteFiles.length) {
        try {
          console.warn('these are still uploading', incompleteFiles);
          await this.$confirm('Some files have not finished uploading. Are you sure you want to continue?');
        } catch (e) {
          // user hit cancel
          return;
        }
      }

      // lookup unique filename generated in handleUploadRequest method
      this.documentSubmission.files = files.map(file => this.renamedFileTracker[file.uid])
        .filter(fileName => fileName != null);

      try {
        this.submitting = true;
        const responseToRequest = new RequestResponseCreateDto({
          requestId: this.request.requestId,
          accessCode: this.accessCodeForm.data.accessCode,
          message: this.documentSubmission.message,
          files: this.documentSubmission.files,
        });

        await api.docutax.submitReplyToRequest(this.requestUrlSlug, responseToRequest);
        this.responseIsComplete = true;
      } catch (e) {
        console.error('Submission failed', e);
        this.$message.error('Failed to submit your documents, please try again');
      } finally {
        this.submitting = false;
      }
    },
    returnToHome() {
      this.$router.push({ name: 'home' });
    },
  },
};
</script>

<style lang="scss">
.el-main.send-docutax-request {
  display: block;
  padding: 18px 18px 100px 18px;

  .upload-section {
    max-width: 700px;
    margin: 0 auto;

    .uploader .el-upload {
      width: 100%;

      .el-upload-dragger {
        width: 100%;
        max-width: 100%;
      }
    }
  }
}

.el-dialog.select-office-dialog {
  width: 80%;
  max-width: 900px;

  .zipcode-search {
    .zipcode-form-item {
      max-width: 200px;

      .input-button {
        background-color: #293896 !important;
        color: #fff !important;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        font-weight: bold;
      }
    }
  }

  .search-empty-state {
    font-style: italic;
    color: #999;
    padding: 24px;
    text-align: center;
    border: 1px dotted #ccc;

    &.no-results-found {
      font-style: normal;
      color: #333;
    }
  }
}
</style>
